import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Location } from '~commonInfra/utils/Location';
import { Tableau } from '~commonInfra/utils/TableauLog';
import { CompositeSessionInfoSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';

import { Messages } from '../../../codegen/Localize';
import { toTaskDetailsViewModel } from './adapters/taskDetailsAdapter';
import { IJobHistoryItemDTO, IJobHistoryItemVM } from './IJobHistory';
import { JobHistoryAdapter } from './jobHistoryAdapter';
import { JobHistoryApi } from './JobHistoryApi';
import { TaskDetailsService } from './services/TaskDetailsService';
import { TaskRunNowService } from './services/TaskRunNowService';
import { TaskDetailsSpace } from './TaskDetailsSpace';
import { ITaskDetailsViewModel } from './types/ITaskDetails';

export interface TaskDetailsSpaceContainerProps {
  /** LUID of the task being shown, parsed from the route. */
  readonly taskLuid: string;
  /** Href back to the Jobs page. */
  readonly jobsUrl: string;
  /** Id of the job that navigated here, parsed from the route's `jobId` query param. Consumed by S08-b for autoscroll. */
  readonly jobId?: string | null;
}

interface TaskDetailsSpaceContainerState {
  readonly isLoading: boolean;
  readonly hasError: boolean;
  readonly taskDetails: ITaskDetailsViewModel | null;
  readonly jobHistoryItems: ReadonlyArray<IJobHistoryItemVM>;
  readonly currentPage: number;
  readonly totalCount: number;
  readonly isLoadingMoreHistory: boolean;
  readonly hasHistoryLoadError: boolean;
  readonly hasMorePages: boolean;
  /** True while a "Run Now" request for this task is in flight (Epic 264 stretch). */
  readonly isRunningNow: boolean;
}

const DEFAULT_PAGE_SIZE = 100;
// Bounds the "keep paginating until we find the target job" loop (S08-b, PRD 2.3.9).
const MAX_JOB_SEARCH_PAGES = 3;

/**
 * Snapshot returned by {@link TaskDetailsSpaceContainer.loadNextHistoryPage} so the job-search loop
 * reads the merged result directly instead of a post-await `this.state` (stale under React 18 batching).
 */
interface HistoryLoadOutcome {
  /** A network fetch was actually issued (false when a guard short-circuited to a no-op). */
  readonly fetched: boolean;
  /** The fetched page was merged into state (false when a post-await guard bailed: unmount / task change / error). */
  readonly merged: boolean;
  /** Loaded history rows after this call (used to test whether the target job has now arrived). */
  readonly jobs: ReadonlyArray<IJobHistoryItemVM>;
  /** Whether more pages remain, snapshotted after this call. */
  readonly hasMorePages: boolean;
  /** Whether the history load is in an error state, snapshotted after this call. */
  readonly hasError: boolean;
}

/**
 * Container for the Task Details page.
 *
 * Owns the loading/error state and the "return to Jobs" navigation, fetches the task
 * metadata via {@link TaskDetailsService}, and handles job history pagination.
 */
export class TaskDetailsSpaceContainer extends React.Component<TaskDetailsSpaceContainerProps, TaskDetailsSpaceContainerState> {
  private isMounted_ = false;
  private isLoadingMoreRequest = false;
  // Sync re-entrancy guard for Run Now (mirrors isLoadingMoreRequest). `this.state.isRunningNow` is
  // async under React batching, so a fast double-click could open two confirmation dialogs before it
  // flips; this instance flag is set synchronously in onRunNow and closes that window.
  private isRunNowRequest = false;
  // Resolves with the in-flight page load's outcome (null when none) so the job search can wait out a
  // colliding user-driven load and adopt its result instead of abandoning.
  private historyLoadInFlight: Promise<HistoryLoadOutcome> | null = null;
  private resolveHistoryLoadInFlight: ((outcome: HistoryLoadOutcome) => void) | null = null;
  private seenJobIds = new Set<string>();
  private jobSearchPagesFetched = 0;
  private isSearchingForJob = false;
  // Bumped on jobId/task change; a suspended search whose captured generation no longer matches
  // abandons quietly instead of clobbering the newer search.
  private searchGeneration = 0;
  private loggedNotFoundForJobId: string | null = null;

  public constructor(props: TaskDetailsSpaceContainerProps) {
    super(props);
    this.state = {
      isLoading: !!props.taskLuid,
      hasError: !props.taskLuid,
      taskDetails: null,
      jobHistoryItems: [],
      currentPage: 0,
      totalCount: 0,
      isLoadingMoreHistory: false,
      hasHistoryLoadError: false,
      hasMorePages: true,
      isRunningNow: false,
    };
  }

  public componentDidMount() {
    this.isMounted_ = true;
    this.loadTaskDetails();
  }

  public componentDidUpdate(prevProps: TaskDetailsSpaceContainerProps) {
    // The route (/jobs/task/:taskLuid) reuses this component instance when the user navigates
    // from one task to another, so componentDidMount does not fire again — refetch here.
    // loadTaskDetails owns the state reset, so we don't setState here (avoids a double update).
    if (prevProps.taskLuid !== this.props.taskLuid) {
      this.loadTaskDetails();
      return;
    }
    // A new jobId (without a taskLuid change) re-arms the "keep paginating until found" search,
    // but doesn't warrant refetching task details.
    if (prevProps.jobId !== this.props.jobId) {
      this.resetJobSearch();
    }
  }

  // Resets the search bookkeeping and bumps the generation so a new search can start while a prior
  // one is still suspended (the stale one won't re-clear the guard — its generation no longer matches).
  private resetJobSearch(): void {
    this.jobSearchPagesFetched = 0;
    this.searchGeneration++;
    this.isSearchingForJob = false;
    this.loggedNotFoundForJobId = null;
  }

  public componentWillUnmount() {
    this.isMounted_ = false;
  }

  public render() {
    const taskDetails = this.state.taskDetails;
    // Require the numeric taskId as well: without it the wire call can't resolve the task, so the
    // button would be a dead no-op. Self-hide instead of showing a button that does nothing.
    const canRunNow = !!taskDetails && !!taskDetails.taskId && TaskRunNowService.canRunNow(taskDetails.taskType);
    // Match the Tasks-tab menu: keep Run Now visible for a runnable type but disable (grey) it when
    // the server/site Run Now setting is off. Same rule the menu uses (hasRunNowPermission).
    //
    // v1 permission-parity limitation (§7.3): unlike the Tasks-tab menu's `isClickable`, we do NOT
    // additionally gate on per-item `availableActions` (content permission / legacy-bridge /
    // unpublished-workbook) — the `getTaskDetails` contract does not return `availableActions`, so
    // that data isn't in scope on this page. This is acceptable for v1 because the page is admin-only
    // AND the backend re-authorizes every run by numeric id (extract:
    // verifyUserCanPerformExtractRefreshNow; flow: flowService.runFlowTask), so an unauthorized or
    // otherwise-non-runnable task fails server-side and surfaces as a failure toast (see onRunNow).
    const runNowDisabled = !this.isRunNowEnabledBySetting();
    return (
      <TaskDetailsSpace
        taskLuid={this.props.taskLuid}
        isLoading={this.state.isLoading}
        hasError={this.state.hasError}
        taskDetails={taskDetails}
        jobHistoryItems={this.state.jobHistoryItems}
        isLoadingMoreHistory={this.state.isLoadingMoreHistory}
        hasMoreHistory={this.state.hasMorePages && this.state.jobHistoryItems.length < this.state.totalCount && !this.state.hasHistoryLoadError}
        hasHistoryLoadError={this.state.hasHistoryLoadError}
        onLoadMoreHistory={this.loadNextHistoryPage}
        onRetryLoadMoreHistory={this.retryLoadNextHistoryPage}
        onReturnToJobs={this.onReturnToJobs}
        jobId={this.props.jobId}
        onRequestMoreForJob={this.requestMorePagesForJob}
        canRunNow={canRunNow}
        isRunningNow={this.state.isRunningNow}
        runNowDisabled={runNowDisabled}
        onRunNow={this.onRunNow}
      />
    );
  }

  // Server/site "Run Now" setting gate, read from the session store via the same selector the
  // Tasks-tab menu uses (hasRunNowPermission -> CompositeSessionInfoSelectors.isRunNowEnabled). It
  // returns true when the enforcement feature flag is off, and otherwise (server && site) enabled,
  // OR the viewer is a server admin / online site admin. A per-session setting, so reading on render
  // is fine (no live subscription needed). Backend still enforces SITE_RUN_NOW_DISABLED as a backstop.
  private isRunNowEnabledBySetting(): boolean {
    return CompositeSessionInfoSelectors.isRunNowEnabled(getAppStore().getState());
  }

  // Enqueues a single "Run Now" for this task (Epic 264 stretch), gated to match the Tasks-tab menu:
  //  - a confirmation dialog is shown first (H1 UX parity), so a click never silently enqueues a job;
  //  - success/failure feedback matches the menu — a success toast on a clean enqueue, and an explicit
  //    failure toast on a resolved-with-errors result (H2: the reused wire client suppresses its own
  //    toast for that shape, e.g. SITE_RUN_NOW_DISABLED).
  // Re-entry is closed by the sync `isRunNowRequest` flag (plus the `isRunningNow` state and the type/
  // setting gate). Returns void: the button click doesn't await it.
  private onRunNow = (): void => {
    const taskDetails = this.state.taskDetails;
    // Run Now resolves the task by its numeric id (tasks.id), not the route LUID — see
    // TaskRunNowService.runTaskNow. Bail if it's absent so we never fire a silent no-op.
    const taskId = taskDetails?.taskId;
    if (
      !taskDetails
      || !taskId
      || this.isRunNowRequest
      || this.state.isRunningNow
      || !TaskRunNowService.canRunNow(taskDetails.taskType)
      // Defense-in-depth: the button is already disabled when the setting is off, but re-check so a
      // programmatic invoke can't bypass the same gate the Tasks-tab menu enforces.
      || !this.isRunNowEnabledBySetting()
    ) {
      return;
    }
    const taskType = taskDetails.taskType;
    this.isRunNowRequest = true;
    this.setState({ isRunningNow: true });

    const settle = (): void => {
      this.isRunNowRequest = false;
      if (this.isMounted_) {
        this.setState({ isRunningNow: false });
      }
    };

    TaskRunNowService.confirmAndRunTaskNow(taskType, taskId, taskDetails.contentName, taskDetails.contentType)
      .then(result => {
        if (this.isMounted_) {
          const failed = !!result.errors && result.errors.length > 0;
          ToasterService.getInstance().pop(
            failed
              ? { type: ToastType.Error, text: Messages.runNowFailed({ NUM_TASKS: '1' }) }
              : { type: ToastType.Info, text: Messages.runNowSucceeded({ NUM_TASKS: '1' }) }
          );
        }
        settle();
      })
      .catch((error: unknown) => {
        // Reject == the admin cancelled/dismissed the dialog OR the action threw. ReactActionDialog
        // dismisses with 'cancel' on failure too, so the two are indistinguishable — do NOT toast
        // here (a false error on a plain cancel is worse than silence). Genuine transport failures
        // are already toasted by the error-notifying client behind ExtractTasks/RunFlowTasks; the
        // resolved-with-errors shape is toasted in the .then above. Log for diagnostics only.
        if (error) {
          Tableau.Logger().info(`TaskDetailsSpaceContainer: Run Now not completed for task ${taskId}: ${error}`);
        }
        settle();
      });
  };

  private async loadTaskDetails(): Promise<void> {
    const taskLuid = this.props.taskLuid;
    if (!taskLuid) {
      this.seenJobIds = new Set();
      this.resetJobSearch();
      this.setState({
        isLoading: false,
        hasError: true,
        taskDetails: null,
        jobHistoryItems: [],
        currentPage: 0,
        totalCount: 0,
        hasHistoryLoadError: false,
        hasMorePages: true,
      });
      return;
    }
    this.seenJobIds = new Set();
    this.resetJobSearch();
    this.setState({
      isLoading: true,
      hasError: false,
      taskDetails: null,
      jobHistoryItems: [],
      currentPage: 0,
      totalCount: 0,
      isLoadingMoreHistory: false,
      hasHistoryLoadError: false,
      hasMorePages: true,
    });

    try {
      // Load task details and first page of history in parallel, but let history fail independently.
      const [taskDetailsResult, firstHistoryPageResult] = await Promise.all([
        TaskDetailsService.getTaskDetails(taskLuid)
          .then(value => ({ status: 'fulfilled' as const, value }))
          .catch(() => ({ status: 'rejected' as const })),
        JobHistoryApi.getJobHistoryForTask(taskLuid, 1, DEFAULT_PAGE_SIZE, false)
          .then(value => ({ status: 'fulfilled' as const, value }))
          .catch(() => ({ status: 'rejected' as const })),
      ]);

      if (!this.isMounted_ || this.props.taskLuid !== taskLuid) {
        return;
      }

      if (taskDetailsResult.status === 'rejected') {
        this.setState({
          isLoading: false,
          hasError: true,
          taskDetails: null,
          jobHistoryItems: [],
          currentPage: 0,
          totalCount: 0,
          hasHistoryLoadError: false,
          hasMorePages: true,
        });
        return;
      }

      if (taskDetailsResult.value.errors && taskDetailsResult.value.errors.length > 0) {
        this.setState({
          isLoading: false,
          hasError: true,
          taskDetails: null,
          jobHistoryItems: [],
          currentPage: 0,
          totalCount: 0,
          hasHistoryLoadError: false,
          hasMorePages: true,
        });
        return;
      }

      if (firstHistoryPageResult.status === 'fulfilled') {
        const firstHistoryPage = firstHistoryPageResult.value;
        firstHistoryPage.jobs.forEach(job => this.seenJobIds.add(job.jobId));
        this.setState({
          isLoading: false,
          hasError: false,
          taskDetails: toTaskDetailsViewModel(taskDetailsResult.value),
          jobHistoryItems: JobHistoryAdapter.toSortedViewModels(firstHistoryPage.jobs),
          currentPage: firstHistoryPage.pageNumber,
          totalCount: firstHistoryPage.totalCount,
          hasHistoryLoadError: false,
          hasMorePages: firstHistoryPage.jobs.length >= DEFAULT_PAGE_SIZE,
        });
        return;
      }

      this.setState({
        isLoading: false,
        hasError: false,
        taskDetails: toTaskDetailsViewModel(taskDetailsResult.value),
        jobHistoryItems: [],
        currentPage: 0,
        totalCount: 0,
        hasHistoryLoadError: true,
        hasMorePages: true,
      });
    } catch (error) {
      if (this.isMounted_ && this.props.taskLuid === taskLuid) {
        this.setState({
          isLoading: false,
          hasError: true,
          taskDetails: null,
          jobHistoryItems: [],
          currentPage: 0,
          totalCount: 0,
          hasHistoryLoadError: false,
          hasMorePages: true,
        });
      }
    }
  }

  // Loads the next page. Returns a HistoryLoadOutcome: `fetched` distinguishes a real fetch from a
  // guarded no-op, `merged` whether it was integrated (false if a post-await guard bailed).
  private loadNextHistoryPage = async (): Promise<HistoryLoadOutcome> => {
    if (
      !this.props.taskLuid
      || this.isLoadingMoreRequest
      || this.state.isLoadingMoreHistory
      || this.state.hasHistoryLoadError
      || this.state.jobHistoryItems.length >= this.state.totalCount
    ) {
      return {
        fetched: false,
        merged: false,
        jobs: this.state.jobHistoryItems,
        hasMorePages: this.state.hasMorePages,
        hasError: this.state.hasHistoryLoadError,
      };
    }

    const taskLuid = this.props.taskLuid;
    this.isLoadingMoreRequest = true;
    // Publish the in-flight signal so a concurrent bounded job search can await this load rather
    // than giving up when it collides with a user-driven scroll load.
    this.historyLoadInFlight = new Promise<HistoryLoadOutcome>(resolve => { this.resolveHistoryLoadInFlight = resolve; });
    this.setState({ isLoadingMoreHistory: true });
    const nextPageNumber = this.state.currentPage + 1;
    // Default outcome = fetched but not merged; overwritten on a successful merge or an error below.
    let outcome: HistoryLoadOutcome = {
      fetched: true,
      merged: false,
      jobs: this.state.jobHistoryItems,
      hasMorePages: this.state.hasMorePages,
      hasError: this.state.hasHistoryLoadError,
    };
    try {
      const nextPage = await JobHistoryApi.getJobHistoryForTask(taskLuid, nextPageNumber, DEFAULT_PAGE_SIZE);
      if (!this.isMounted_ || this.props.taskLuid !== taskLuid) {
        return outcome;
      }
      const newDtos: IJobHistoryItemDTO[] = nextPage.jobs.filter(job => !this.seenJobIds.has(job.jobId));
      newDtos.forEach(job => this.seenJobIds.add(job.jobId));
      const mergedJobs = JobHistoryAdapter.mergeSortedPage(this.state.jobHistoryItems, newDtos);
      const hasMorePages = nextPage.jobs.length >= DEFAULT_PAGE_SIZE;
      outcome = { fetched: true, merged: true, jobs: mergedJobs, hasMorePages, hasError: false };
      this.setState({
        jobHistoryItems: mergedJobs,
        currentPage: nextPage.pageNumber,
        totalCount: nextPage.totalCount,
        isLoadingMoreHistory: false,
        hasHistoryLoadError: false,
        hasMorePages,
      });
    } catch (error) {
      if (this.isMounted_ && this.props.taskLuid === taskLuid) {
        this.setState({ isLoadingMoreHistory: false, hasHistoryLoadError: true });
      }
      outcome = { fetched: true, merged: false, jobs: this.state.jobHistoryItems, hasMorePages: this.state.hasMorePages, hasError: true };
    } finally {
      this.isLoadingMoreRequest = false;
      // Release any waiter with this load's outcome; null the fields first so a resumed waiter sees
      // no in-flight load and can fetch the next page.
      const releaseWaiter = this.resolveHistoryLoadInFlight;
      this.historyLoadInFlight = null;
      this.resolveHistoryLoadInFlight = null;
      releaseWaiter?.(outcome);
    }
    return outcome;
  };

  // Pages until the target job appears or a termination condition is hit. The loop drives itself off
  // each call's HistoryLoadOutcome (React-18-safe) and a generation token drops a superseded search.
  private requestMorePagesForJob = async (): Promise<void> => {
    const jobId = this.props.jobId;
    if (!jobId || this.isSearchingForJob || this.state.jobHistoryItems.some(job => job.jobId === jobId)) {
      return;
    }
    const generation = this.searchGeneration;
    this.isSearchingForJob = true;
    // Local snapshot of the termination inputs, seeded from state and refreshed only from returned
    // outcomes (never re-read from this.state after an await — see HistoryLoadOutcome).
    let jobs: ReadonlyArray<IJobHistoryItemVM> = this.state.jobHistoryItems;
    let hasMorePages = this.state.hasMorePages;
    let hasError = this.state.hasHistoryLoadError;
    try {
      while (
        this.searchGeneration === generation
        && this.props.jobId === jobId
        && !jobs.some(job => job.jobId === jobId)
        && this.jobSearchPagesFetched < MAX_JOB_SEARCH_PAGES
        && hasMorePages
        && !hasError
      ) {
        const outcome = await this.loadNextHistoryPage();
        // A newer search (or a task switch) superseded us while we were awaiting — abandon quietly;
        // the finally leaves the newer search's isSearchingForJob guard untouched.
        if (this.searchGeneration !== generation) {
          return;
        }
        if (outcome.merged) {
          this.jobSearchPagesFetched++;
          jobs = outcome.jobs;
          hasMorePages = outcome.hasMorePages;
          hasError = outcome.hasError;
          continue;
        }
        if (outcome.fetched) {
          // Fetched but not merged (unmount / task change / error) — terminal for this search.
          hasError = outcome.hasError;
          break;
        }
        // No-op fetch: if a user-driven load is in flight, wait it out and adopt its result without
        // charging the budget; otherwise the short-circuit is terminal.
        const pendingLoad = this.historyLoadInFlight;
        if (!pendingLoad) {
          break;
        }
        const pendingOutcome = await pendingLoad;
        if (this.searchGeneration !== generation) {
          return;
        }
        jobs = pendingOutcome.jobs;
        hasMorePages = pendingOutcome.hasMorePages;
        hasError = pendingOutcome.hasError;
      }
      const exhausted = this.jobSearchPagesFetched >= MAX_JOB_SEARCH_PAGES || !hasMorePages || hasError;
      if (
        this.searchGeneration === generation
        && this.props.jobId === jobId
        && !jobs.some(job => job.jobId === jobId)
        && exhausted
        && this.loggedNotFoundForJobId !== jobId
      ) {
        this.loggedNotFoundForJobId = jobId;
        Tableau.Logger().info(`TaskDetailsSpaceContainer: job ${jobId} not found within the job search page limit.`);
      }
    } finally {
      // Only release the guard if we're still the current search; a newer search that superseded us
      // owns it now (this runs on every exit path, including the mid-await returns).
      if (this.searchGeneration === generation) {
        this.isSearchingForJob = false;
      }
    }
  };

  private retryLoadNextHistoryPage = async (): Promise<void> => {
    if (!this.isMounted_) {
      return;
    }
    this.setState({ hasHistoryLoadError: false }, this.loadNextHistoryPage);
  };

  private onReturnToJobs = (): void => {
    Location.instance.setUrl(this.props.jobsUrl);
  };
}
